import ContentTypes from "../../../../../../shared/lib/html5-software-library/src/ranj/eng/content/ContentTypes";
import KeyMomentChoiceContent from "../../../../../../shared/lib/html5-software-library/src/ranj/eng/content/KeyMomentChoiceContent";
import VariableMutationNumberContent from "../../../../../../shared/lib/html5-software-library/src/ranj/eng/content/VariableMutationNumberContent";
import DefaultDialogPlayerEvent from "../../../../../../shared/lib/html5-software-library/src/ranj/eng/dialog/framework/player/DefaultDialogPlayerEvent";
import HierarchicalDialogPlayer from "../../../../../../shared/lib/html5-software-library/src/ranj/eng/dialog/framework/player/HierarchicalDialogPlayer";
import PlayMode from "../../../../../../shared/lib/html5-software-library/src/ranj/eng/dialog/framework/player/PlayMode";
import { isNull } from "../../../../../../shared/lib/html5-software-library/src/ranj/util/TypeUtil";
import GameEvent from "../../../../../../shared/src/ranj/app/core/GameEvent";
import GameConfig from "../../core/model/GameConfig";
import SingleChoiceData from "../../core/model/SingleChoiceData";
import NGTConfigKey from "../base/NGTConfigKey";
import ResumableDefaultDialogPlayer from "./ResumableDefaultDialogPlayer";
import NGTDialogPlayerEvent from "./NGTDialogPlayerEvent";
import DialogMemento from "./DialogMemento";
import DialogContentComponent from "../../../../../../shared/lib/html5-software-library/src/ranj/eng/content/DialogContentComponent";
import SaveEvent from "../../../../../../shared/src/ranj/equalture/savegame/SaveEvent";
import Config from "../../../../../../shared/src/ranj/app/config/Config";
import PlaySessionModel from "../playsession/PlaySessionModel";
import MultipleChoiceData from "../../core/model/MultipleChoiceData";
import { DialogOption } from "../../../../../../shared/lib/html5-software-library/src/ranj/eng/dialog/engine/model/Dialog";

export default class CollaborationDialogPlayer {

    /**
     * 
     * @param {*} variableManager 
     * @param {PlaySessionModel} playSessionModel 
     * @param {*} languagePack 
     * @param {Config} config 
     * @param {soma.EventDispatcher} dispatcher 
     * @param {GameConfig} gameConfig 
     */
    constructor(variableManager, playSessionModel, languagePack, config, dispatcher, gameConfig) {

        this.eventDispatcher = dispatcher;

        this._dialogPlayer = new ResumableDefaultDialogPlayer(100);

        this._currentOptions = [];
        this._currentImageId = null;
        this._choicesMade = [];
        this._openContent = [];
        this._chosenTexts = [];
        this._contentDelayedOptionMap = {};

        this._handlePauseGameBound = this._handlePauseGame.bind(this);
        this._handleResumeGameBound = this._handleResumeGame.bind(this);

        // Keep a reference to the injected objects
        this._variableManager = variableManager;
        this._playSessionModel = playSessionModel;
        this._languagePack = languagePack;
        this._config = config;
        this._gameConfig = gameConfig;

        this._history = [];

        this._isCommandSequenceEnd = false;
        this._isPlayingContent = false;
    }

    destroy() {
        this.removeEventListeners();
        this._dialogPlayer.destroy();
        this._dialogPlayer = null;
    }

    get dialogMemento() {
        const commandSequence = this._dialogPlayer.getCurrentCommandSequence();
        return new DialogMemento(this.getContent().getLabel(), commandSequence.id, this._currentImageId);
    }

    /**
     * 
     * @param {DialogMemento} dialogMemento 
     */
    loadMemento(dialogMemento) {
        this.currentImageId = dialogMemento.imageId;
        this._dialogPlayer.jumpToCommandSequence(dialogMemento.commandSequenceId);
    }

    back() {
        if (this._history.length > 1) {
            this._history.pop();//current state
            const dialogMemento = this._history.pop();//previous state
            this._currentOptions = [];
            this._currentImageId = null;
            this.loadMemento(dialogMemento);
            return dialogMemento;
        }
        return null;
    }

    /**
     * @param {String} value
     */
    set currentImageId(value) {
        this._currentImageId = value;
    }

    /**
     * @returns {DialogContentComponent}
     */
    getContent() {
        return this._content;
    }
    /**
     * @param {DialogContentComponent} content 
     */
    setContent(content) {
        this._content = content;
    }

    getContentPlayer() {
        return this._contentPlayerDelegate;
    }

    setContentPlayer(contentPlayer) {
        this._contentPlayerDelegate = contentPlayer;
    }

    /**
     * @returns {HierarchicalDialogPlayer}
     */
    getDialogPlayer() {
        return this._dialogPlayer;
    }

    setDialog(dialog) {
        this._dialogPlayer.setDialog(dialog, this._variableManager.solve.bind(this._variableManager));
        this._dialogPlayer.setPlayMode(PlayMode.COMMANDS);
    }

    playContent() {
        // NB: guard against double invocation - addEventListeners() does not deduplicate,
        // so a second call would double all dialog event handling (e.g. duplicating options).
        if (this._isPlayingContent) {
            return;
        }
        this._isPlayingContent = true;
        this.addEventListeners();
        this._dialogPlayer.next();
    }

    stop() {
        this.removeEventListeners();
    }

    contentCompleted(content) {
        // Check whether the given content was indeed open in the dialog player
        // and advance if all open content has been completed.
        // Only case where more than one content may be open is if the CTD dialog player
        // adds content to the content coming from the underlying Ranj dialog player.

        const foundContent = this._openContent.contains(content);
        if (foundContent) {
            this._openContent.remove(content);
        }

        // Timeout: check if we need to take a timeout option now.
        const timeoutTaken = this.checkTakeTimeoutOption();
        if (timeoutTaken) {
            return;
        }

        // Handling:
        // - If this content delayed the picking of an option, pick it now
        // - If this content was "just" open, do a next() on the dialog player
        if (this._contentDelayedOptionMap[content]) {
            const option = this._contentDelayedOptionMap[content];
            delete this._contentDelayedOptionMap[content];
            this._dialogPlayer.takeOption(option);
        } else {
            if (this._openContent.length === 0 && foundContent) {
                // All open content is completed: next!
                this._dialogPlayer.next();
            }
        }
    }

    getCurrentOptions() {
        return this._currentOptions;
    }

    /**
     * 
     * @param {DialogOption} submitOption 
     * @param {KeyMomentChoiceContent[]} selectedContents 
     */
    multipleOptionsChosen(submitOption, selectedContents) {
        this.trackMultipleOptionsChosen(selectedContents);
        this._takeOption(submitOption);
    }

    /**
     * 
     * @param {DialogOption} option 
     */
    optionChosen(option) {
        const content = option.getData()?.getData()?.content;
        this.trackOptionChosen(content);
        this._takeOption(option);
    }

    /**
     * 
     * @param {DialogOption} option 
     */
    _takeOption(option) {
        this.processOptionConsequences(option);
        const content = option.getData().getData().content;
        // Auto-audio
        const contentType = content.getType();
        if (contentType === ContentTypes.TYPE_TEXT) {
            this._chosenTexts.push(content);
        }

        // Pick the option immediately.
        this._dialogPlayer.takeOption(option);

        // For the outside world, the option is just chosen (i.e. option buttons can be removed, option panels can be made invisible)
        // regardless of whether audio is being played before the dialog advances.
        const dialogPlayerEvent = new NGTDialogPlayerEvent(NGTDialogPlayerEvent.DIALOG_OPTION_CHOSEN, null, option);
        this.eventDispatcher.dispatch(NGTDialogPlayerEvent.DIALOG_OPTION_CHOSEN, { data: dialogPlayerEvent });

        this.clearOptions();
    }

    addEventListeners() {

        this._dialogPlayer.addEventListener(DefaultDialogPlayerEvent.START_DIALOG, this.handleStartDialog, this);
        this._dialogPlayer.addEventListener(DefaultDialogPlayerEvent.CONTENT, this.handleDialogContent, this);
        this._dialogPlayer.addEventListener(DefaultDialogPlayerEvent.SCORE, this.handleDialogScore, this);
        this._dialogPlayer.addEventListener(DefaultDialogPlayerEvent.DIALOG_COMMAND, this.handleDialogCommand, this);
        this._dialogPlayer.addEventListener(DefaultDialogPlayerEvent.DIALOG_OPTION, this.handleDialogOption, this);
        this._dialogPlayer.addEventListener(DefaultDialogPlayerEvent.EXIT_DIALOG_STATE, this.handleExitDialogState, this);
        this._dialogPlayer.addEventListener(DefaultDialogPlayerEvent.ENTER_DIALOG_STATE, this.handleEnterDialogState, this);
        this._dialogPlayer.addEventListener(DefaultDialogPlayerEvent.COMMAND_SEQUENCE_END, this.handleDialogStateEnd, this);
        this._dialogPlayer.addEventListener(DefaultDialogPlayerEvent.AUTO_TAKE_OPTION_TAKEN, this.handleAutoTakeOptionTaken, this);
        this._dialogPlayer.addEventListener(DefaultDialogPlayerEvent.AUTO_TAKE_TIME_TICK, this.handleAutoTakeTimeTick, this);
        this._dialogPlayer.addEventListener(DefaultDialogPlayerEvent.DIALOG_TIME_TICK, this.handleDialogTimeTick, this);
        this._dialogPlayer.addEventListener(DefaultDialogPlayerEvent.DIALOG_TIME_OUT, this.handleDialogTimeOut, this);
        this._dialogPlayer.addEventListener(DefaultDialogPlayerEvent.END_DIALOG, this.handleEndDialog, this);

        // Listen to game pause/resume
        this.eventDispatcher.addEventListener(GameEvent.PAUSE_GAME, this._handlePauseGameBound);
        this.eventDispatcher.addEventListener(GameEvent.RESUME_GAME, this._handleResumeGameBound);

    }

    removeEventListeners() {

        this._dialogPlayer.removeEventListener(DefaultDialogPlayerEvent.START_DIALOG, this.handleStartDialog, this);
        this._dialogPlayer.removeEventListener(DefaultDialogPlayerEvent.CONTENT, this.handleDialogContent, this);
        this._dialogPlayer.removeEventListener(DefaultDialogPlayerEvent.SCORE, this.handleDialogScore, this);
        this._dialogPlayer.removeEventListener(DefaultDialogPlayerEvent.DIALOG_COMMAND, this.handleDialogCommand, this);
        this._dialogPlayer.removeEventListener(DefaultDialogPlayerEvent.DIALOG_OPTION, this.handleDialogOption, this);
        this._dialogPlayer.removeEventListener(DefaultDialogPlayerEvent.EXIT_DIALOG_STATE, this.handleExitDialogState, this);
        this._dialogPlayer.removeEventListener(DefaultDialogPlayerEvent.ENTER_DIALOG_STATE, this.handleEnterDialogState, this);
        this._dialogPlayer.removeEventListener(DefaultDialogPlayerEvent.COMMAND_SEQUENCE_END, this.handleDialogStateEnd, this);
        this._dialogPlayer.removeEventListener(DefaultDialogPlayerEvent.AUTO_TAKE_OPTION_TAKEN, this.handleAutoTakeOptionTaken, this);
        this._dialogPlayer.removeEventListener(DefaultDialogPlayerEvent.AUTO_TAKE_TIME_TICK, this.handleAutoTakeTimeTick, this);
        this._dialogPlayer.removeEventListener(DefaultDialogPlayerEvent.DIALOG_TIME_TICK, this.handleDialogTimeTick, this);
        this._dialogPlayer.removeEventListener(DefaultDialogPlayerEvent.DIALOG_TIME_OUT, this.handleDialogTimeOut, this);
        this._dialogPlayer.removeEventListener(DefaultDialogPlayerEvent.END_DIALOG, this.handleEndDialog, this);

        this.eventDispatcher.removeEventListener(GameEvent.PAUSE_GAME, this._handlePauseGameBound);
        this.eventDispatcher.removeEventListener(GameEvent.RESUME_GAME, this._handleResumeGameBound);

        this._isPlayingContent = false;

    }

    handleStartDialog(event) {
        console.info("Dialog started.");
    }

    _handlePauseGame(event) {
        this.setPaused(true);
    }

    _handleResumeGame(event) {
        this.setPaused(false);
    }

    setPaused(value) {
        if (value) {
            this._dialogPlayer.pauseTimers();
        } else {
            this._dialogPlayer.resumeTimers();
        }
    }

    handleDialogContent({ data, option }) {
        const content = data.content;

        // Remember this content as open (so that completion can lead to next())
        this._openContent.push(content);

        switch (content.getType()) {
            case ContentTypes.TYPE_SET_VARIABLE:
                this.handleSetVariableContent(content);
                break;
            case ContentTypes.TYPE_SET_NUMBER:
                this.handleSetNumberContent(content);
                break;
            case ContentTypes.TYPE_TEXT: {
                if (this.eventDispatcher.hasEventListener(NGTDialogPlayerEvent.DIALOG_CONTENT)) {
                    //Auto audio
                    const dialogContentPackage = data;
                    const textContent = dialogContentPackage.content;
                    const contentID = textContent.getContentID();
                    // Dispatch text content AFTER auto-audio, otherwise the underlying dialog will advance even before the audio is finished.
                    var dialogPlayerEvent = new NGTDialogPlayerEvent(NGTDialogPlayerEvent.DIALOG_CONTENT, data, option);
                    this.eventDispatcher.dispatch(NGTDialogPlayerEvent.DIALOG_CONTENT, { data: dialogPlayerEvent });

                    // Remember this text as the "current situation"
                    this._currentSituation = (contentID && contentID.length > 0) ? contentID : textContent.getText();


                } else {
                    this.ignoreContent(content);
                }
                break;
            }
            case ContentTypes.TYPE_IMAGE:
                this.currentImageId = content.getImage();
            //and fall through!:
            case ContentTypes.TYPE_MINIGAME:
            case ContentTypes.TYPE_ALLEGATION:
            case ContentTypes.TYPE_AUDIO:
            case ContentTypes.TYPE_OVERLAY_IMAGE:
            case ContentTypes.TYPE_KEY_MOMENT_CHOICE:
            case ContentTypes.TYPE_MOVIE: {
                if (this.eventDispatcher.hasEventListener(NGTDialogPlayerEvent.DIALOG_CONTENT)) {
                    const dialogPlayerEvent = new NGTDialogPlayerEvent(NGTDialogPlayerEvent.DIALOG_CONTENT, data, option);
                    this.eventDispatcher.dispatch(NGTDialogPlayerEvent.DIALOG_CONTENT, { data: dialogPlayerEvent });
                } else {
                    this.ignoreContent(content);
                }
                break;
            }
            default:
                this.contentCompleted(content);
                break;
        }
    }

    handleEnterDialogState() {
        this._isCommandSequenceEnd = false;
        this._history.push(this.dialogMemento);
        this.eventDispatcher.dispatch(SaveEvent.SAVE);
    }

    handleSetVariableContent(content) {
        const variableMutations = content.getVariableMutations();
        for (let i = 0; i < variableMutations.length; i++) {
            const mut = variableMutations[i];
            this._variableManager.setVariable(mut.variableName, mut.value, mut.mutationType);
        }

        this.contentCompleted(content);
    }

    handleSetNumberContent(content) {
        this._variableManager.setNumber(content.name, content.value, content.operator);
        this.contentCompleted(content);
    }

    handleDialogScore({ data }) {
        this.contentCompleted(data);
    }

    handleDialogCommand({ data }) {
        this.contentCompleted(data);
    }

    handleDialogOption({ data }) {
        this._dialogPlayer.next();

        // Add if not an autotake option
        if (data.option.getData() !== null && data.option.getData().content !== null) {
            this.addOption(data.option);
            const dialogPlayerEvent = new NGTDialogPlayerEvent(NGTDialogPlayerEvent.DIALOG_OPTION, data.data, data.option);
            this.eventDispatcher.dispatch(NGTDialogPlayerEvent.DIALOG_OPTION, { data: dialogPlayerEvent });
        }
    }

    processOptionConsequences(option) {
        // Do whatever is necessary because this option was chosen.
        const content = option?.getData()?.getData()?.content;
        if (!content) {
            return;
        }

        if (content.getType() === ContentTypes.TYPE_KEY_MOMENT_CHOICE) {
            // Process variables
            const varMutations = content.getScoreVariables();
            for (let i = 0; i < varMutations.length; i++) {
                /** @type {VariableMutationNumberContent} */
                const mutation = varMutations[i];
                this._variableManager.setVariable(mutation.getVariableName(), mutation.getValue(), mutation.getMutationType());
            }

            // Remember key moment choices made in this dialog
            this._choicesMade.push(content);
        }
    }

    /**
     * 
     * @param {KeyMomentChoiceContent[]} optionContents 
     */
    trackMultipleOptionsChosen(optionContents) {
        const dialogId = this.getContent().getDialogID();
        const episodeId = this._playSessionModel.getCurrentScenarioSession().getScenarioID();
        // Conventions:
        // keyMomentId is used for situationId
        const situationId = optionContents[0].getKeyMomentID();
        // situation description is used for 'option id'
        const optionIds = optionContents.map(content => content.getSituationDescription());
        // time to answer is calculated
        const timeToAnswer = Date.now() - this._timeOfLastOption;

        // Track interaction event
        const optionsData = new MultipleChoiceData(episodeId, dialogId, situationId, optionIds, timeToAnswer);
        if (ranj.DEBUG) {
            console.log(`[CollaborationDialogPlayer] Multiple Options chosen: ${JSON.stringify(optionsData, null, 2)}`, optionsData);
        }
        this.eventDispatcher.dispatch(CollaborationDialogPlayer.MULTIPLE_OPTIONS_CHOSEN, optionsData);
    }

    trackOptionChosen(optionContent) {
        if (!optionContent || optionContent.getType() !== ContentTypes.TYPE_KEY_MOMENT_CHOICE) {
            return;
        }
        /**@type {KeyMomentChoiceContent} */
        const keyMomentChoiceContent = optionContent;
        const dialogId = this.getContent().getDialogID();
        const episodeId = this._playSessionModel.getCurrentScenarioSession().getScenarioID();
        // Conventions:
        // keyMomentId is used for situationId
        const situationId = keyMomentChoiceContent.getKeyMomentID();
        // situation description is used for 'option id'
        const optionId = keyMomentChoiceContent.getSituationDescription();
        // time to answer is calculated
        const timeToAnswer = Date.now() - this._timeOfLastOption;


        // Track interaction event
        const optionData = new SingleChoiceData(episodeId, dialogId, situationId, optionId, timeToAnswer);
        if (ranj.DEBUG) {
            console.log(`[CollaborationDialogPlayer] Option chosen: ${JSON.stringify(optionData, null, 2)}`, optionData);
        }
        this.eventDispatcher.dispatch(CollaborationDialogPlayer.OPTION_CHOSEN, optionData);
    }

    addAutoVoiceOver(audioID) {
        const audioContent = new AudioContent(this._languagePack, null, audioID);
        const audioDialogContentPackage = new DialogContentPackage(this._languagePack, audioContent, null);

        // Audio content is now also "open"
        this._openContent.push(audioContent);

        const dialogPlayerEvent = new NGTDialogPlayerEvent(NGTDialogPlayerEvent.DIALOG_CONTENT, audioDialogContentPackage, null);
        this.eventDispatcher.dispatch(NGTDialogPlayerEvent.DIALOG_CONTENT, { data: dialogPlayerEvent });
        return audioContent;
    }

    handleDialogStateEnd(event) {
        if(this._isCommandSequenceEnd) {
            return;
        }
        
        this._isCommandSequenceEnd = true;

        if (this.getCurrentOptions().length > 0) {
            this.eventDispatcher.dispatch(NGTDialogPlayerEvent.DIALOG_OPTIONS_CHANGED, { data: {} });
        }
    }

    handleExitDialogState(event) {
        this.clearOptions();
    }

    handleAutoTakeTimeTick({ data }) {
        this.eventDispatcher.dispatch(NGTDialogPlayerEvent.ANSWER_TIMER, new NGTDialogPlayerEvent(NGTDialogPlayerEvent.ANSWER_TIMER, data));

    }

    handleDialogTimeTick({ data }) {
        this.eventDispatcher.dispatch(NGTDialogPlayerEvent.DIALOG_TIMER, new NGTDialogPlayerEvent(NGTDialogPlayerEvent.DIALOG_TIMER, this._dialogPlayer.getMaxTime() - data));
    }

    handleDialogTimeOut({ data }) {
        this._timeoutOption = data;
        this.checkTakeTimeoutOption();
    }

    handleEndDialog(event) {

        // Process choices made (if any)
        for (let i = 0; i < this._choicesMade.length; i++) {
            const kmChoiceContent = this._choicesMade[i];
            this._playSessionModel.getCurrentScenarioSession().addKeyMomentEntry(kmChoiceContent);
        }
        this.removeEventListeners();
        const dialogPlayerEvent = new NGTDialogPlayerEvent(NGTDialogPlayerEvent.DIALOG_END);
        this.eventDispatcher.dispatch(NGTDialogPlayerEvent.DIALOG_END, { data: dialogPlayerEvent });
    }

    completeContent() {
        this.getContentPlayer().contentCompleted(this.getContent());
    }

    handleAutoTakeOptionTaken({ data }) {
        //Remove from history since stepping back beyond autotake doesnt work
        this._history.pop();
        this.processOptionConsequences(data.option);
    }

    addOption(option) {
        this.getCurrentOptions().push(option);
        this._timeOfLastOption = Date.now();
    }

    clearOptions() {
        if (this._currentOptions.length === 0)
            return;

        this._currentOptions.length = 0;
        this.eventDispatcher.dispatch(NGTDialogPlayerEvent.DIALOG_OPTIONS_CHANGED, { data: {} });
    }

    checkTakeTimeoutOption() {
        if (!isNull(this._timeoutOption) && this._openContent.length === 0) {
            this.takeTimeoutOption();
            return true;
        }

        return false;
    }

    takeTimeoutOption() {
        this.eventDispatcher.dispatch(new NGTDialogPlayerEvent(NGTDialogPlayerEvent.DIALOG_TIME_OUT));
        this._dialogPlayer.takeOption(this._timeoutOption);
        this._timeoutOption = null;
    }

    ignoreContent(content) {
        console.warn(`WARNING: content type ${content.type} not handled by game!`);
        this.contentCompleted(content);
    }

    hasChosenText(content) {
        for (let i = 0; i < this._chosenTexts.length; i++) {
            if (this._chosenTexts[i] === content) //maybe use other compare on string or ID
            {
                return true;
            }
        }
        return false;
    }
}

CollaborationDialogPlayer.OPTION_CHOSEN = "CollaborationDialogPlayer.OPTION_CHOSEN";
CollaborationDialogPlayer.MULTIPLE_OPTIONS_CHOSEN = "CollaborationDialogPlayer.MULTIPLE_OPTIONS_CHOSEN";